import type { SavedAccountSession } from './accountSessions.storage'

export function normalizeAccountEmail(email: string): string {
  return email.trim().toLowerCase()
}

function sessionQualityScore(session: SavedAccountSession): number {
  let score = 0
  if (session.refreshToken) score += 100
  if (session.accessToken) score += 50
  if (session.zportiAvatarId) score += 10
  if (session.displayName) score += 5
  score += session.expiresAt / 1e15
  return score
}

function mergeSavedAccountSessions(
  a: SavedAccountSession,
  b: SavedAccountSession,
  keepUserId?: string,
): SavedAccountSession {
  const preferred = sessionQualityScore(a) >= sessionQualityScore(b) ? a : b
  const other = preferred === a ? b : a
  return {
    userId: keepUserId ?? preferred.userId,
    email: preferred.email || other.email,
    displayName: preferred.displayName || other.displayName,
    avatarUrl: preferred.avatarUrl || other.avatarUrl,
    zportiAvatarId: preferred.zportiAvatarId || other.zportiAvatarId,
    zportiAvatarConfig: preferred.zportiAvatarConfig || other.zportiAvatarConfig,
    accessToken: preferred.accessToken || other.accessToken,
    refreshToken: preferred.refreshToken || other.refreshToken,
    expiresAt: Math.max(preferred.expiresAt, other.expiresAt),
  }
}

function pickPreferredSession(
  a: SavedAccountSession,
  b: SavedAccountSession,
  activeUserId?: string | null,
): SavedAccountSession {
  if (activeUserId) {
    if (a.userId === activeUserId) return mergeSavedAccountSessions(a, b, a.userId)
    if (b.userId === activeUserId) return mergeSavedAccountSessions(b, a, b.userId)
  }
  return mergeSavedAccountSessions(a, b)
}

export function hasDuplicateSavedAccountSessions(sessions: SavedAccountSession[]): boolean {
  const userIds = new Set<string>()
  const emails = new Set<string>()

  for (const session of sessions) {
    if (!session.userId) continue
    if (userIds.has(session.userId)) return true
    userIds.add(session.userId)

    const email = normalizeAccountEmail(session.email)
    if (!email) continue
    if (emails.has(email)) return true
    emails.add(email)
  }

  return false
}

/**
 * Collapse duplicate stored accounts (same userId or same normalized email).
 * Keeps the best session tokens/metadata when merging duplicates.
 */
export function dedupeSavedAccountSessions(
  sessions: SavedAccountSession[],
  activeUserId?: string | null,
): SavedAccountSession[] {
  const byUserId = new Map<string, SavedAccountSession>()

  for (const session of sessions) {
    if (!session.userId) continue
    const existing = byUserId.get(session.userId)
    byUserId.set(
      session.userId,
      existing ? pickPreferredSession(existing, session, activeUserId) : session,
    )
  }

  const byEmail = new Map<string, SavedAccountSession>()
  const withoutEmail: SavedAccountSession[] = []

  for (const session of byUserId.values()) {
    const email = normalizeAccountEmail(session.email)
    if (!email) {
      withoutEmail.push(session)
      continue
    }

    const existing = byEmail.get(email)
    byEmail.set(
      email,
      existing ? pickPreferredSession(existing, session, activeUserId) : session,
    )
  }

  return [...byEmail.values(), ...withoutEmail]
}
